import { motion } from "framer-motion";
import { ArrowRight, Aperture, FlaskConical, Gauge, MoveRight } from "lucide-react";
import { services } from "@/data/brand";
import { TrustSection } from "@/components/immersive/TrustSection";
import { MatrixRain } from "@/components/MatrixRain";
import { InstagramFeed } from "@/components/social/InstagramFeed";
import "@/matrix-hero.css";

const Fade = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay }}>{children}</motion.div>
);

export default function Home({ onQuote }) {
  return (
    <main data-testid="home-page">
      <section className="hero matrix-home-hero" data-testid="home-hero-section">
        <img src="/media/presentation/page-11-image-01-clean.webp" alt="MADMINUTE rider accelerating through an off-road course" className="hero-image" />
        <MatrixRain />
        <div className="hero-overlay" />
        <div className="hero-speedline" />
        <div className="hero-content">
          <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="hero-eyebrow">Automotive R&D · Production · Motorsport culture</motion.p>
          <motion.h1 initial={{ opacity: 0, x: -45 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .6 }} data-testid="hero-heading">FUEL YOUR<br /><span>ADRENALIN RUSH.</span></motion.h1>
          <motion.p className="hero-copy" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .3 }} data-testid="hero-description">Driven by passion. Powered by innovation. We engineer, capture, and create the world around speed.</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .5 }}>
            <button className="button button-primary" onClick={() => onQuote("General")} data-testid="hero-request-quote-button">Request a quote <ArrowRight size={18} /></button>
            <a className="button button-ghost" href="/store" data-testid="hero-shop-gear-link">Explore gear <MoveRight size={18} /></a>
          </motion.div>
        </div>
        <div className="hero-stat" data-testid="hero-project-count"><strong>40+</strong><span>projects delivered</span></div>
      </section>

      <section className="intro-section section-pad" data-testid="home-intro-section">
        <Fade className="section-number">/ 01</Fade>
        <Fade className="intro-copy">
          <p className="eyebrow">Built for forward motion</p>
          <h2>ONE TEAM.<br />THREE <span>HIGH-PERFORMANCE</span> WORLDS.</h2>
        </Fade>
        <Fade className="intro-detail" delay={.15}>
          <p>MADMINUTE connects engineering precision, cinematic storytelling, and motorsports culture under one roof.</p>
          <p>From hybrid and electric vehicle testing to race-day production and apparel, every project is designed to push performance forward.</p>
        </Fade>
      </section>

      <section className="services-section section-pad" id="services" data-testid="services-section">
        <div className="section-heading-row"><div><p className="eyebrow">Capabilities</p><h2>WHAT WE DO</h2></div><p data-testid="services-summary">Strategy to execution. Lab to field. Brief to final frame.</p></div>
        <div className="service-list">
          {services.map((service, index) => (
            <Fade delay={index * .05} key={service.title}>
              <a href={service.link} className="service-row" data-testid={`service-${index + 1}-link`}>
                <span>{service.number}</span><h3>{service.title}</h3><p>{service.copy}</p><ArrowRight />
              </a>
            </Fade>
          ))}
        </div>
      </section>

      <section className="feature-grid" data-testid="home-feature-grid">
        <a href="/production" className="feature-card feature-production" data-testid="production-feature-link">
          <img loading="lazy" src="/media/presentation/page-08-image-02.webp" alt="MADMINUTE motorsport production capturing a live jump" />
          <div className="feature-shade"/><div className="feature-content"><Aperture/><p className="eyebrow">Production</p><h2>THE THRILL,<br />IN EVERY FRAME.</h2><span>Explore production <ArrowRight size={17}/></span></div>
        </a>
        <a href="/rnd" className="feature-card feature-rnd" data-testid="rnd-feature-link">
          <img loading="lazy" src="/media/presentation/page-08-image-04.webp" alt="Off-road vehicle operating in a demanding water crossing" />
          <div className="feature-shade"/><div className="feature-content"><FlaskConical/><p className="eyebrow">Automotive R&D</p><h2>TEST. LEARN.<br />OUTPERFORM.</h2><span>Explore R&D <ArrowRight size={17}/></span></div>
        </a>
      </section>

      <section className="field-gallery section-pad" data-testid="field-gallery-section">
        <div className="section-heading-row"><div><p className="eyebrow">Selected field work</p><h2>MADE IN MOTION</h2></div><p data-testid="field-gallery-summary">Approved moments from off-road programs, rally environments, and live motorsport production.</p></div>
        <div className="field-grid">
          <figure data-testid="field-gallery-item-1"><img loading="lazy" src="/media/presentation/page-08-image-01.webp" alt="Off-road vehicle moving through a water test"/><figcaption><span>R&D / Field</span><strong>Water-crossing evaluation</strong></figcaption></figure>
          <figure data-testid="field-gallery-item-2"><img loading="lazy" src="/media/presentation/page-06-image-03.webp" alt="Off-road rider lifting the front wheel on a dirt trail"/><figcaption><span>Production / Action</span><strong>Trail story in motion</strong></figcaption></figure>
          <figure data-testid="field-gallery-item-3"><img loading="lazy" src="/media/presentation/page-08-image-05.webp" alt="Off-road vehicle emerging from a water crossing"/><figcaption><span>Automotive / Terrain</span><strong>Real-world capability</strong></figcaption></figure>
          <figure data-testid="field-gallery-item-4"><img loading="lazy" src="/media/presentation/page-08-image-03.webp" alt="Motocross rider performing during a live event"/><figcaption><span>Motorsport / Live</span><strong>Event energy captured</strong></figcaption></figure>
        </div>
      </section>

      <InstagramFeed />

      <section className="proof-section section-pad" data-testid="proof-section">
        <div><p className="eyebrow">Why MADMINUTE</p><h2>FIELD-TESTED.<br /><span>STORY-DRIVEN.</span></h2></div>
        <div className="proof-grid">
          <div data-testid="proof-projects"><Gauge/><strong>40+</strong><span>Projects managed and delivered</span></div>
          <div data-testid="proof-scope"><FlaskConical/><strong>360°</strong><span>Engineering, management, and media</span></div>
          <div data-testid="proof-energy"><Aperture/><strong>01</strong><span>Integrated team from field to final cut</span></div>
        </div>
      </section>
      <TrustSection />
    </main>
  );
}